import type { QueryClient } from '@tanstack/vue-query';
import type { ComputedRef } from 'vue';
import type {
  ISearchKeywordsResponse,
  ISearchMatchedKeywords
} from '@/types/services/search.service';
import type { IFilterSizeStep } from '@/types/composables/filter-shared/useFilterDetails';

export const getLatestSearchQuery = (queryClient: QueryClient, userIndex: ComputedRef<string>) => {
  const searchQueries = queryClient.getQueriesData({
    queryKey: ['authenticatedQuery', userIndex, 'search', 'query'],
    type: 'active'
  });
  // Take the first element of the matched search queries. This is the most recent search query.
  // Take the first element from that array. This is the query key.
  // Take the 5th element of that array. This is the search query.
  const searchQuery = searchQueries.length ? searchQueries[0]?.[0]?.[4] : '';
  if (typeof searchQuery !== 'string') {
    throw new Error('Search query is not a string');
  }
  return searchQuery;
};

export const getMatchingSearchKeywords = (
  searchValue: string,
  searchKeywords: ISearchKeywordsResponse
) => {
  const cleanedSearchValue = searchValue.trim();
  const matchingKeywords = Object.entries(searchKeywords).reduce<ISearchMatchedKeywords>(
    (acc, [key, value]) => {
      const cleanedValue = value.slice(1, -1);
      const regex = new RegExp(cleanedValue, 'i');
      const res = regex.exec(cleanedSearchValue);
      if (res) {
        acc[key as keyof ISearchKeywordsResponse] = res;
      }
      return acc;
    },
    {}
  );
  const fromToOrRegex = new RegExp(
    `(${searchKeywords.from.slice(1, -1)}|${searchKeywords.to.slice(1, -1)})(\\s+or\\s+)(${searchKeywords.from.slice(1, -1)}|${searchKeywords.to.slice(1, -1)})`,
    'i'
  );

  const fromToAndOrRes = fromToOrRegex.exec(cleanedSearchValue);
  if (fromToAndOrRes) {
    matchingKeywords.fromToAndOr = fromToAndOrRes;
  }
  return matchingKeywords;
};

export const isSearchSizeStepValid = (step: string): step is IFilterSizeStep => {
  return ['B', 'KB', 'MB'].includes(step);
};

export const cleanMultiWordSearchQuery = (searchQuery: string | undefined): string | undefined => {
  if (!searchQuery) {
    return undefined;
  }
  // Check if the first and last character are used for grouping a query. If so then remove them only returning the multi word query itself.
  if (
    (searchQuery.startsWith('"') && searchQuery.endsWith('"')) ||
    (searchQuery.startsWith('(') && searchQuery.endsWith(')')) ||
    (searchQuery.startsWith("'") && searchQuery.endsWith("'"))
  ) {
    return searchQuery.slice(1, -1).trim();
  }
  // If the query is not grouped then return it as is.
  return searchQuery;
};

export const addMultiWordGrouping = (searchQuery: string): string => {
  // Check if the query needs grouping, aka it has any whitespace in it.
  if (!searchQuery.includes(' ')) {
    return searchQuery.trim();
  }
  // Check if the search query is already grouped. If so then return it as is.
  if (
    (searchQuery.startsWith('"') && searchQuery.endsWith('"')) ||
    (searchQuery.startsWith('(') && searchQuery.endsWith(')')) ||
    (searchQuery.startsWith("'") && searchQuery.endsWith("'"))
  ) {
    return searchQuery;
  }
  // If the query is not grouped then add grouping to it.
  return `"${searchQuery.trim()}"`;
};
